import { ReactComponent as Dropdown } from "../../../../assets/icons/dropdown.svg";
import { ReactComponent as PrintOn } from "../../../../assets/icons/printOn.svg";
import { ReactComponent as PrintOff } from "../../../../assets/icons/printOff.svg";
import { ReactComponent as Empty } from "../../../../../common/assets/icons/empty.svg";
import { OtherBranchData } from "../../../../../common/hooks/console/otherBranch/useGetOtherBranchCustomer";
import React, { useEffect, useState } from "react";
import { useGetOtherBranchPayment } from "../../../../../common/hooks/console/otherBranch/useGetOtherBranchPayment";
import Input from "../../../../components/common/input";
import Button from "../../../../../common/components/button";
import { ReactComponent as LeftArrow } from "../../../../assets/icons/leftArrow.svg";
import { ReactComponent as RightArrow } from "../../../../assets/icons/rightArrow.svg";
import { ReactComponent as Memo } from "../../../../assets/icons/memo.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/closeIcon.svg";
import { useAuthStore } from "../../../../store/useAuthStore";
import useToastStore from "../../../../store/useToastStore";
import { useSystemPopStore } from "../../../../store/useSystemPopStore";
import { usePutSalesMemo } from "../../../../../common/hooks/console/sales/usePutSalesMemo";
import { usePutOtherBranchMemo } from "../../../../../common/hooks/console/otherBranch/usePutOtherBranchMemo";
import DayPickerInput from "../../../../components/common/DayPickerInput";
import SelectBox from "../../../../components/common/selectBox";
import { useGetCommonPayMethod } from "../../../../../common/hooks/common/useGetCommonPayMethod";
import { usePostPaymentSales } from "../../../../../common/hooks/console/sales/usePostSales";
import { usePostPaymentOtherBranch } from "../../../../../common/hooks/console/otherBranch/usePostOtherBranch";
import { usePutOrderReceipt } from "../../../../../common/hooks/console/transaction/usePutOrderReceipt";

type OBDetailTableProps = {
    data?: OtherBranchData[];
    onSort: (field: string) => void;
    customerIdx: number;
};

const OBDetailTable = ({ data = [], onSort, customerIdx }: OBDetailTableProps) => {
    const { addToast } = useToastStore();
    const { showPop } = useSystemPopStore();
    const level = useAuthStore(state => state.m_level);
    const name = useAuthStore(state => state.m_name);

    const [orderIdx, setOrderIdx] = useState<number>(0);
    const [historyPop, setHistoryPop] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [inputValue, setInputValue] = useState<string>(currentPage?.toString() || "");
    const [openMemoIdx, setOpenMemoIdx] = useState<number | null>(null);
    const [memo, setMemo] = useState<string>("");
    const [paymentPop, setPaymentPop] = useState<boolean>(false);
    const [memoInput, setMemoInput] = useState<string>("");
    const [payAmount, setPayAmount] = useState<string>("");
    const [payDate, setPayDate] = useState<Date | undefined>(undefined);
    const [method, setMethod] = useState<string>("");
    const [methodValue, setMethodValue] = useState<string>("");
    const [payDateError, setPayDateError] = useState<boolean>(false);
    const [methodError, setMethodError] = useState<boolean>(false);
    const [payAmountError, setPayAmountError] = useState<boolean>(false);

    const { data: methodData } = useGetCommonPayMethod({ payment_category: 1 });
    const { mutate } = usePutOrderReceipt();

    const { data: paymentData, refetch } = useGetOtherBranchPayment({
        customerIdx: customerIdx,
        orderIdx: orderIdx,
        page: currentPage,
    });
    const { mutate: postOBMutate } = usePostPaymentOtherBranch(customerIdx, orderIdx, undefined);

    const { mutate: memoModifyMutate } = usePutOtherBranchMemo(() => {
        setOpenMemoIdx(null);
        addToast({
            type: "success",
            message: "메모가 수정되었습니다.",
        });
    });

    const handleOrderClick = (newOrderIdx: number) => {
        setOrderIdx(newOrderIdx);
        setHistoryPop(true);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setInputValue(page.toString());
    };

    const handlePageInput = (value: string) => {
        if (value.trim() === "") {
            setInputValue("");
            return;
        }
        if (!/^\d*$/.test(value)) {
            return;
        }
        const pageNumber = Number(value.trim());
        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > (paymentData?.last_page || 1)) {
            alert("페이지 범위를 벗어났습니다.");
            setInputValue(value.slice(0, -1));
            return;
        }

        setInputValue(value);
        handlePageChange(pageNumber);
    };

    const handleMemoModify = (customerId: number, paymentHistoryIdx: number) => {
        memo &&
            memoModifyMutate({
                customerId,
                paymentHistoryIdx,
                customerMemoData: { payment_memo: memo },
            });
    };

    const handleChange = (e: any) => {
        let rawValue = e.target.value.replace(/[^0-9]/g, "");

        if (rawValue === "") {
            setPayAmount("");
            return;
        }

        const numericValue = Number(rawValue);
        setPayAmount(numericValue.toLocaleString());
    };

    const handleSubmit = () => {
        let hasError = false;
        if (!payDate) {
            setPayDateError(true);
            hasError = true;
        }
        if (!method.trim()) {
            setMethodError(true);
            hasError = true;
        }
        if (!payAmount.trim()) {
            setPayAmountError(true);
            hasError = true;
        }
        if (hasError) return;

        const getFormattedDate = (date: Date | undefined) => {
            if (!date) return "";
            return date.toLocaleDateString("sv-SE");
        };

        postOBMutate(
            {
                payment_date: getFormattedDate(payDate),
                payment_method: method,
                payment_amount: Number(payAmount.replace(/,/g, "")),
                payment_memo: memoInput,
            },
            {
                onSuccess: data => {
                    setPayDate(undefined);
                    setMethod("");
                    setMethodValue("");
                    setPayAmount("");
                    setMemoInput("");
                    setPayDate(undefined);
                    setPaymentPop(false);

                    addToast({
                        type: "success",
                        message: "입금 내역을 등록하었습니다.",
                    });
                },
                onError: (error: any) => {
                    const errorMessage = error?.response?.data?.message || "알 수 없는 오류가 발생했습니다.";
                    showPop({
                        title: errorMessage,
                        rightButton: undefined,
                        type: "negative",
                        onRightClick: () => {},
                    });
                },
            },
        );
    };

    useEffect(() => {
        if (orderIdx !== 0) {
            refetch();
        }
    }, [orderIdx, refetch]);

    return (
        <div className="h-full overflow-auto">
            <table className="table-auto w-full text-sm">
                {/* Header */}
                <thead className="bg-primary3 border-b border-gray3 shadow-md h-[60px]">
                    <tr>
                        <th className="border-b min-w-[190px] border-b-gray3 border-r border-r-gray4 px-3 text-center">
                            <div className="flex flex-col gap-1 items-center">
                                <div className="flex items-center gap-1 ml-1">
                                    <p className="text-base">배송일자</p>
                                    <p className="text-gray2 font-normal">배송시간</p>
                                    <Dropdown className="cursor-pointer" onClick={() => onSort("delivery_date")} />
                                </div>
                                <p className="text-gray2 font-normal">주문상태 및 인수증 출력</p>
                            </div>
                        </th>
                        <th className="border-b border-b-gray3 font-normal border-r border-r-gray4 w-1/2">
                            <div className="grid grid-cols-[minmax(220px,_1fr)_minmax(220px,_1fr)] items-center h-[30px]">
                                <div className="flex items-center h-[30px] min-w-[220px] px-2 border-r border-gray4 justify-between">
                                    <span>주문자</span>
                                    <Dropdown className="cursor-pointer" onClick={() => onSort("order_name")} />
                                </div>
                                <div className="flex px-2 w-full justify-between min-w-[220px] items-center">
                                    <span>상품</span>
                                    <Dropdown className="cursor-pointer" onClick={() => onSort("product_name")} />
                                </div>
                            </div>

                            <div className="grid grid-cols-[minmax(220px,_1fr)_minmax(220px,_1fr)] h-[30px] font-normal items-center border-t border-gray4">
                                <p className="min-w-[220px] h-full flex items-center px-2 border-r border-gray4 justify-start">명의</p>
                                <p className="h-[30px] pt-1 flex justify-start px-2 border-t border-gray4">배송주소</p>
                            </div>
                        </th>
                        <th className="min-w-[510px] w-full">
                            <div className="flex h-[30px] font-normal w-full">
                                <div className="px-3 h-full flex items-center justify-center flex-1 border-r">판매금액</div>
                                <div className="px-3 h-full flex items-center justify-center flex-1 border-r">발주금액</div>
                                <div className="px-3 h-full flex items-center justify-center flex-1 border-r">미정산금액</div>
                                <div className="px-3 h-full flex items-center justify-center min-w-[150px] w-[150px]">정산상태</div>
                            </div>
                            <div className="flex h-[30px] font-normal items-center border-t border-gray4">
                                <div className="w-1/3 h-full flex items-center px-2 justify-start">받으시는 분</div>
                            </div>
                        </th>
                    </tr>
                </thead>

                {/* Body */}
                <tbody>
                    {data && data.length > 0 ? (
                        data.map((item, index) => (
                            <tr key={`${item.customer_idx}-${index}`}>
                                <td className="border border-gray4 px-3 text-center">
                                    <div className="flex flex-col text-gray2 gap-1 items-center">
                                        <div className="flex gap-1 items-center">
                                            <p className="text-[15px] text-gray1 font-bold">{item?.order_number}</p>
                                            <p className="whitespace-nowrap">{item?.delivery_date}</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div
                                                className={`py-[3px] rounded-3xl text-[13px] whitespace-nowrap px-2 font-bold ${
                                                    item.order_status === 1
                                                        ? "bg-[#FFC700] text-white  px-[14px]"
                                                        : item.order_status === 2
                                                          ? "border border-success text-success"
                                                          : item.order_status === 3
                                                            ? "border border-warning text-warning"
                                                            : item.order_status === 4
                                                              ? "border border-skyblue text-skyblue"
                                                              : item.order_status === 5
                                                                ? "border border-grayicon text-grayicon"
                                                                : item.order_status === 6
                                                                  ? "bg-gray4 px-5 text-gray3"
                                                                  : item.order_status === 7
                                                                    ? "bg-error px-5 text-white"
                                                                    : ""
                                                }`}
                                            >
                                                {item?.order_status_text}
                                            </div>
                                            {item?.is_printed ? <PrintOn /> : <PrintOff />}
                                            <p className="text-[13px] whitespace-nowrap">{item?.customer_manager_name}</p>
                                        </div>
                                        <div className="text-sm font-bold translate-y-[3px] flex items-center">
                                            청구일자 {item?.excel_date ? item?.excel_date : "-"}
                                        </div>
                                    </div>
                                </td>
                                <td className="border border-gray4 text-left">
                                    <div className="grid grid-cols-[minmax(220px,_1fr)_minmax(220px,_1fr)] items-center border-b border-gray4">
                                        <div className="min-w-[220px] min-h-12 border-r border-gray4 px-2 py-1.5">
                                            <p className="">{item?.customer_name}</p>
                                            <p className="text-gray2">
                                                {item?.order_name} {item?.order_mobile}
                                            </p>
                                        </div>
                                        <div className="flex p-2 w-20 flex-grow w-full">
                                            <span className="text-gray2 break-keep">{item?.product_name}</span>
                                        </div>
                                    </div>
                                    <div className="min-h-12 grid grid-cols-[minmax(220px,_1fr)_minmax(220px,_1fr)] items-center">
                                        <p className={`min-w-[220px] h-full border-r border-gray4 flex items-center p-2 justify-start`}>
                                            {item?.wreath_sender}
                                        </p>
                                        <p
                                            className="min-w-[220px] p-2 text-gray2"
                                            // style={{
                                            //     display: "-webkit-box",
                                            //     WebkitLineClamp: 2,
                                            //     WebkitBoxOrient: "vertical",
                                            //     overflow: "hidden",
                                            //     textOverflow: "ellipsis",
                                            // }}
                                        >
                                            {item?.delivery_address} {item?.delivery_address_detail}
                                        </p>
                                    </div>
                                </td>
                                <td className="border-b align-top">
                                    <div className="flex flex-col">
                                        <div className="flex">
                                            <div className="px-2 flex justify-end items-center flex-1 h-12 bg-primary4 border-r border-gray4">
                                                {item?.sales_amount.toLocaleString()}원
                                            </div>
                                            <div className="px-2 flex justify-end items-center flex-1 border-r border-gray4 bg-primary4">
                                                {item?.order_amount.toLocaleString()}원
                                            </div>
                                            <div
                                                onClick={() => handleOrderClick(item?.order_idx)}
                                                className="px-2 flex text-error underline cursor-pointer justify-end items-center flex-1 border-r border-gray4 bg-primary4"
                                            >
                                                {item?.pending_amount?.toLocaleString()}원
                                            </div>
                                            <div className="w-[150px] flex gap-1 justify-center items-center font-medium">
                                                <p
                                                    className={`${item?.branch_payment_status_text === "미정산" ? "text-error" : "text-skyblue"} px-2 text-nowrap`}
                                                >
                                                    {item.pending_amount === 0 && item.branch_payment_status_text === "미정산"
                                                        ? "-"
                                                        : item.branch_payment_status_text}
                                                </p>
                                                {level !== 7 && item?.branch_payment_status_text === "정산완료" && (
                                                    <Button
                                                        onClick={() => {
                                                            setOrderIdx(item?.order_idx);
                                                            showPop({
                                                                title: "정산완료된 상태를 <br /> 취소하시겠습니까?",
                                                                content: "현재 저장된 정산금액은 <br /> 0원으로 초기화됩니다.",
                                                                rightButton: "정산취소",
                                                                type: "negative",
                                                                onRightClick: () => {
                                                                    postOBMutate(
                                                                        {
                                                                            payment_date: new Date().toISOString().split("T")[0],
                                                                            payment_amount: -Math.abs(item?.payment_amount),
                                                                        },
                                                                        {
                                                                            onSuccess: () => {
                                                                                setPayDate(undefined);
                                                                                setMethod("");
                                                                                setMethodValue("");
                                                                                setPayAmount("");
                                                                                setMemoInput("");
                                                                                setPaymentPop(false);

                                                                                addToast({
                                                                                    type: "error",
                                                                                    message: "정산취소하였습니다.",
                                                                                });
                                                                            },
                                                                            onError: (error: any) => {
                                                                                const errorMessage =
                                                                                    error?.response?.data?.message ||
                                                                                    "알 수 없는 오류가 발생했습니다.";
                                                                                showPop({
                                                                                    title: errorMessage,
                                                                                    rightButton: undefined,
                                                                                    type: "negative",
                                                                                    onRightClick: () => {},
                                                                                });
                                                                            },
                                                                        },
                                                                    );
                                                                },
                                                            });
                                                        }}
                                                        size="S"
                                                        status={`${item?.payment_status_text === "미수" ? "regist" : ""}`}
                                                        className="px-2 whitespace-nowrap"
                                                    >
                                                        정산취소
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex w-full h-full border-t border-gray4">
                                            <div className="px-2 min-h-12 flex items-center">
                                                {item?.recipient_name}{" "}
                                                {item.event_content.depth2
                                                    ? item.event_content.depth2
                                                    : item.event_content.depth1 === "직접입력"
                                                      ? item.event_content.event_content
                                                      : ""}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={10} className="text-center">
                                <div className="flex flex-col items-center gap-5 py-4 mt-52">
                                    <Empty />
                                    <p className="text-grayicon text-lg">등록된 내역이 없습니다.</p>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {historyPop && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]"
                    onClick={() => setHistoryPop(false)}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg w-[800px] overflow-hidden px-6 py-5 flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center p-3">
                            <p className="text-2xl font-bold">정산 등록 내역</p>
                            <div className="flex items-center gap-5">
                                <div className="flex items-center gap-2">
                                    <button onClick={() => currentPage && currentPage > 1 && handlePageChange(currentPage - 1)}>
                                        <LeftArrow />
                                    </button>
                                    <Input
                                        inputClassName="text-center"
                                        className="w-20 text-center outline-0 text-sm flex"
                                        value={paymentData?.last_page ? inputValue : "0"}
                                        onChange={e => handlePageInput(e.target.value)}
                                    />
                                    <p className="text-gray3">/ {paymentData?.last_page || 0}</p>
                                    <button
                                        onClick={() =>
                                            currentPage && currentPage < (paymentData?.last_page || 1) && handlePageChange(currentPage + 1)
                                        }
                                    >
                                        <RightArrow />
                                    </button>
                                </div>
                                <CloseIcon
                                    className="cursor-pointer"
                                    onClick={() => {
                                        setCurrentPage(1);
                                        setInputValue("1");
                                        setHistoryPop(false);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="overflow-y-auto h-[55vh]">
                            <table className="text-sm text-gray2 w-full overflow-y-auto">
                                <thead className="h-10  border-b border-gray4">
                                    <tr>
                                        <th className="px-2 py-3 w-1/2 text-start">입금일자</th>
                                        <th className="min-w-[120px] px-2 py-3 text-start">결제수단</th>
                                        <th className="px-2 py-3 w-1/2 text-start">정산금액</th>
                                        <th className="min-w-[100px] px-2 py-3 text-start">담당자</th>
                                        <th className="min-w-[52px] px-2 py-3 text-center">메모</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentData?.data_list && paymentData?.data_list.length > 0 ? (
                                        paymentData?.data_list.map(item => (
                                            <tr className="h-[50px] border-y border-gray4 hover:bg-primary4">
                                                <td className="px-2">{item.payment_date.replaceAll("-", ".")}</td>
                                                <td className={`px-2  ${item.payment_amount < 0 ? "text-error" : ""}`}>
                                                    {item.payment_method || "입금취소"}
                                                </td>
                                                <td className="px-2 text-end bg-primary4">{item.payment_amount.toLocaleString()}원</td>
                                                <td className="px-2">{item.manager_name}</td>
                                                <td className="px-2 text-center mx-auto relative">
                                                    <div
                                                        onClick={() => {
                                                            setOpenMemoIdx(item.payment_history_idx);
                                                            setMemo(item.payment_memo);
                                                        }}
                                                        className="flex justify-center cursor-pointer"
                                                    >
                                                        <Memo />
                                                    </div>
                                                    {openMemoIdx === item.payment_history_idx && (
                                                        <div className="bg-white rounded-2xl z-10  shadow-custom w-[420px] top-10 absolute right-2">
                                                            <div className="flex items-center justify-between px-6 py-5">
                                                                <p className="text-2xl font-bold text-[#1C1C1E]">메모</p>
                                                                <CloseIcon
                                                                    className="cursor-pointer"
                                                                    onClick={() => setOpenMemoIdx(null)}
                                                                />
                                                            </div>
                                                            <div className="flex flex-col px-6 pb-5">
                                                                <textarea
                                                                    maxLength={100}
                                                                    disabled={level === 7 || (level === 8 && item.manager_name !== name)}
                                                                    value={memo}
                                                                    onChange={e => setMemo(e.target.value)}
                                                                    className="h-[120px] border border-gray disabled:mb-3 text-sm px-3 outline-0 rounded-lg py-2 resize-none"
                                                                    placeholder="메모 입력"
                                                                />
                                                                <span
                                                                    className={`text-sm m-[8px_0_20px_auto] ${memo.length === 100 ? "text-error" : "text-gray3 "}`}
                                                                >
                                                                    {memo.length ?? 0}/100
                                                                </span>
                                                                {level !== 7 && (level !== 8 || item.manager_name === name) && (
                                                                    <Button
                                                                        size="S"
                                                                        className="w-[68px] ml-auto"
                                                                        onClick={() =>
                                                                            handleMemoModify(item.customer_idx, item.payment_history_idx)
                                                                        }
                                                                    >
                                                                        메모 수정
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="text-center pt-32 text-grayicon">
                                                등록된 내역이 없습니다.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {paymentPop && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]"
                    onClick={() => setPaymentPop(false)}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg w-[480px] flex justify-center flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center px-6 py-5">
                            <p className="text-2xl font-bold">입금 등록</p>
                            <CloseIcon
                                className="cursor-pointer"
                                onClick={() => {
                                    setMemoInput("");
                                    setMethodValue("");
                                    setPayDate(undefined);
                                    setPayAmount("");
                                    setPaymentPop(false);
                                }}
                            />
                        </div>
                        <div className="bg-gray6 px-5 py-3 flex flex-col gap-2">
                            <div className="flex gap-1">
                                <div
                                    className={`${payDateError && "border-error border"} bg-white py-1.5 px-3 rounded-lg w-full flex items-center justify-between`}
                                >
                                    <p className="text-sm">입금일자</p>
                                    <DayPickerInput
                                        right
                                        initialDate={payDate}
                                        onDateChange={date => {
                                            setPayDateError(false);
                                            setPayDate(date);
                                        }}
                                        className="h-full rounded-lg max-w-[110px]"
                                    />
                                </div>
                                <div
                                    className={`bg-white py-1.5 px-3 rounded-lg w-full border ${methodError ? "border-error" : "border-gray4"} flex items-center gap-2`}
                                >
                                    <p className="text-sm flex-shrink-0">결제수단</p>
                                    <SelectBox
                                        status="view"
                                        className="w-full"
                                        options={
                                            methodData?.data.map(method => ({
                                                value: method.payment_order.toString(),
                                                label: method.payment_name,
                                            })) || []
                                        }
                                        value={methodValue}
                                        onChange={value => {
                                            const selectedMethod = methodData?.data.find(
                                                method => method.payment_order.toString() === value,
                                            );
                                            setMethodValue(value);
                                            setMethod(selectedMethod?.payment_name || "");
                                            setMethodError(false);
                                        }}
                                    />
                                </div>
                            </div>
                            <div
                                className={`bg-white items-center text-sm pl-3 ${payAmountError ? "border-error" : "border-gray4"} rounded-lg flex justify-between border`}
                            >
                                <p>입금액</p>
                                <Input
                                    inputClassName="border-none pb-[4.5px]"
                                    value={payAmount}
                                    onChange={handleChange}
                                    price
                                    className={`font-bold px-3 `}
                                />
                            </div>
                            <Input
                                value={memoInput}
                                onChange={e => setMemoInput(e.target.value)}
                                placeholder="메모입력(100자)"
                                maxLength={100}
                            />
                        </div>
                        <div className="flex px-6 py-4 w-full gap-2">
                            <Button
                                size="L"
                                className="w-full"
                                onClick={() => {
                                    setMemoInput("");
                                    setMethodValue("");
                                    setPayDate(undefined);
                                    setPayAmount("");
                                    setPaymentPop(false);
                                }}
                            >
                                취소
                            </Button>
                            <Button onClick={handleSubmit} size="L" status="regist" className="w-full">
                                입금등록
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OBDetailTable;
